import { useRef, useState } from "react";
import { Camera, Loader2, ShieldCheck, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "./ui/dialog";
import { useCart } from "../context/CartContext";
import { api, track } from "../lib/api";

// Custom 21+ verification — ID photos go to secure object storage and are
// injected into the Ecwid order notes by the backend sweeper. We never use
// Ecwid product file-upload options (they break the cart API).
export function AgeVerifyModal() {
  const { verifyOpen, completeVerification, cancelVerification } = useCart();
  const [form, setForm] = useState({ name: "", email: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !confirmed) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("idPhoto", file);
      const res = await api.verify(fd);
      track("age_verified", { ref: res.ref });
      localStorage.setItem("ccdc_verify_email", form.email.trim().toLowerCase());
      toast.success("Verified — your ID is linked to this order securely.");
      completeVerification(res.ref);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    cancelVerification();
  };

  return (
    <Dialog open={verifyOpen} onOpenChange={(v) => !v && close()}>
      <DialogContent
        data-testid="age-verify-modal"
        aria-describedby={undefined}
        className="max-w-md overflow-hidden rounded-2xl border-brand-line bg-brand-cream p-0 max-sm:h-[92dvh] max-sm:max-h-none max-sm:overflow-y-auto"
      >
        <div className="bg-brand-forest px-6 py-5 text-brand-cream">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2.5 font-display text-xl font-black">
              <ShieldCheck className="h-5 w-5 text-brand-gold" /> 21+ Age Verification
            </h2>
            <button onClick={close} data-testid="age-verify-close" aria-label="Close verification" className="rounded-full p-1.5 hover:bg-white/10">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-brand-cream/70">
            Upload a photo of yourself holding your government ID. It's stored securely, linked to
            your order privately, and checked again at handoff. Use the same email at checkout.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 px-6 py-6">
          <div>
            <label htmlFor="verify-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-brand-ink/50">Full name</label>
            <input
              id="verify-name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="verify-name-input" autoComplete="name"
              className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm outline-none focus:border-brand-gold"
            />
          </div>
          <div>
            <label htmlFor="verify-email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-brand-ink/50">Email (same as checkout)</label>
            <input
              id="verify-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="verify-email-input" autoComplete="email"
              className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm outline-none focus:border-brand-gold"
            />
          </div>
          <div>
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.2em] text-brand-ink/50">Selfie holding your ID</span>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="verify-id-input" />
            {preview ? (
              <button type="button" onClick={() => fileRef.current?.click()} className="relative block w-full overflow-hidden rounded-xl border border-brand-line" data-testid="verify-id-preview">
                <img src={preview} alt="ID verification preview" className="max-h-48 w-full object-cover" />
                <span className="absolute bottom-2 right-2 rounded-full bg-brand-forest/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-cream">
                  Retake
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="verify-id-upload-button"
                className="flex w-full flex-col items-center gap-2.5 rounded-xl border-2 border-dashed border-brand-line bg-white px-4 py-8 text-brand-ink/50 transition-colors hover:border-brand-gold hover:text-brand-pine"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Camera className="h-5 w-5" /> Take or upload photo
                </span>
                <span className="text-xs">JPG, PNG or HEIC · max 10 MB</span>
              </button>
            )}
          </div>
          <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-brand-ink/70">
            <input
              type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="verify-checkbox" className="mt-0.5 h-4 w-4 accent-brand-pine"
            />
            I confirm I am 21 or older and the ID shown is mine and valid.
          </label>
          <button
            type="submit"
            disabled={busy || !file || !confirmed}
            data-testid="verify-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-pine py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream transition-transform hover:scale-[1.01] disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {busy ? "Uploading securely…" : "Verify & Continue to Checkout"}
          </button>
          <p className="text-center text-[10px] leading-relaxed text-brand-ink/45">
            Stored encrypted · visible only to order staff · never used for marketing
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
